import { useCallback } from "react";
import { liveContractMomentOf } from "../lib/forecast/reportPointer.js";
import {
  alternateDistributionResolution,
  contractResolutionSettled,
  withAlternateSpace,
} from "../lib/forecast/alternateSpaces.js";
import { isVoidedContract } from "../lib/forecast/contractStatus.js";
import { useLiveForecastReport } from "./useReportPointer.js";
import { useAlternateResolution } from "./useAlternateResolution.js";

/**
 * The main view's "alternate space by default" overlay (Settings → debug).
 *
 * With `enabled`, reads the active forecast's live report for its `alternate_forecast`
 * and hands back `applyToRun`, which swaps the alternate into a run's distribution for
 * DISPLAY — the lane marker and the framing buffer. A scrubbed FORECAST vintage uses its
 * own alternate (`viewSnap.alternateForecast`), never the live one, and a vintage without
 * one shows its original distribution; a selected resolution entry keeps the live one.
 *
 * `applyToRun` also stamps `distributionResolution` — the contract's verdict re-keyed to
 * the alternate space (see alternateDistributionResolution) — which the lane collapses
 * its bars onto instead of `resolution`. `resolution` itself is untouched: everything
 * else that reads it (concluded / terminal checks) must keep seeing the real verdict.
 *
 * Returns the identity function for `applyToRun` when disabled or when there is no
 * alternate, so callers render exactly as before.
 *
 * @param {{enabled: boolean, active: object|null, viewSnap: object|null,
 *   viewEntry?: object|null}} args
 * @returns {{alternate: object|null, applyToRun: (run: any) => any}}
 */
export function useAlternateSpaceDefault({ enabled, active, viewSnap, viewEntry = null }) {
  const liveSource = useLiveForecastReport({
    reportPointers: active?.reportPointers,
    liveMomentIso: liveContractMomentOf(active),
    enabled: !!enabled && !!active,
  });
  // A resolution entry's snapshot is the verdict, not a forecast vintage.
  const forecastVintage = viewSnap && !viewEntry?.isResolution ? viewSnap : null;
  const alternate = !enabled
    ? null
    : forecastVintage
      ? forecastVintage.alternateForecast || null
      : liveSource?.alternateForecast || null;
  // The alternate answer is the contract's LATEST, so it settles only the at-rest view:
  // on any scrubbed position (a forecast vintage or an older resolution attempt) the
  // alternate bars stay uncollapsed rather than borrow a later winner.
  const altResolution = useAlternateResolution({
    reportRowKey: alternate ? alternate.reportRowKey : null,
    enabled:
      !!alternate &&
      !viewEntry &&
      (contractResolutionSettled(active?.resolution) || isVoidedContract(active)),
  });
  const applyToRun = useCallback(
    (run) => {
      if (!alternate || !run) return run;
      const swapped = withAlternateSpace(run, alternate);
      if (swapped === run) return run;
      return {
        ...swapped,
        distributionResolution: alternateDistributionResolution(
          run.resolution,
          altResolution,
          alternate.spaceKey,
        ),
      };
    },
    [alternate, altResolution],
  );
  return { alternate, applyToRun };
}
